import { useEffect, useRef, type ReactNode } from "react";
import { useFeedbackDock } from "../components/chrome/FeedbackDock";
import { builderCopy } from "./copy";
import { BUILDER_STAGES, type BuilderStageId } from "./stages";

export interface BuilderFrameProps {
  /** The dial's name, once it has one. */
  name: string | null;
  stage: BuilderStageId;
  /** The stages the rail lets the author open; the rest show but can't be pressed. */
  enterable: readonly BuilderStageId[];
  onStage: (stage: BuilderStageId) => void;
  /** Every way out of the builder goes through this, so it can save first. */
  onFinishLater: () => void;
  /** Where the save state reads ("Saved just now"). */
  saveState?: ReactNode;
  children: ReactNode;
}

/**
 * The builder's own screen (KEY-560): no app header, just the dial's name, the five stages and the ways out. The header has no
 * width to spare, and a builder that shares it would let every link in it leave without saving.
 */
export function BuilderFrame({ name, stage, enterable, onStage, onFinishLater, saveState, children }: BuilderFrameProps) {
  const dock = useFeedbackDock();
  const frameRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLElement>(null);

  // The wheel stays in view just under the bar, which grows a row when the stages wrap on a narrow screen.
  useEffect(() => {
    const frame = frameRef.current;
    const bar = barRef.current;
    if (!frame || !bar || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => frame.style.setProperty("--kd-builder-bar-h", `${bar.offsetHeight}px`));
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="kd-builder" ref={frameRef}>
      <header className="kd-builder-bar" ref={barRef}>
        <div className="kd-builder-title">
          <span className="kd-builder-name">{name?.trim() || builderCopy.frame.untitled}</span>
          {saveState && (
            <span className="kd-builder-save" aria-live="polite">
              {saveState}
            </span>
          )}
        </div>

        <nav className="kd-builder-stages" aria-label={builderCopy.frame.stagesLabel}>
          <ol>
            {BUILDER_STAGES.map((id, index) => (
              <li key={id}>
                <button
                  type="button"
                  className="kd-builder-stage"
                  aria-current={id === stage ? "step" : undefined}
                  disabled={!enterable.includes(id)}
                  onClick={() => onStage(id)}
                >
                  <span className="kd-builder-stage-n" aria-hidden="true">
                    {index + 1}
                  </span>
                  {builderCopy.frame.stages[id]}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <div className="kd-builder-exits">
          <button type="button" className="kd-btn" onClick={dock.open}>
            {builderCopy.frame.feedback}
          </button>
          <button type="button" className="kd-btn" onClick={onFinishLater}>
            {builderCopy.frame.saveAndFinishLater}
          </button>
        </div>
      </header>

      <main className="kd-builder-body">{children}</main>
    </div>
  );
}
